import React, { useState, useEffect } from 'react';
import { 
  FaHeart, 
  FaComment, 
  FaShare, 
  FaClock, 
  FaFire,
  FaChartLine,
  FaBuilding,
  FaDollarSign,
  FaArrowUp,
  FaBolt,
  FaGem
} from 'react-icons/fa';
import { 
  MdFeed, 
  MdNotifications,
  MdTrendingUp 
} from 'react-icons/md';
import { 
  IoSparkles, 
  IoFlash, 
  IoTrendingUp as IoTrending,
  IoShareSocial 
} from 'react-icons/io5';
import BottomNavigation from './BottomNavigation';
import { useAuth } from '../security/security/AuthContext';
import axios from 'axios';

const UserFeeds = () => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { userDetails, token } = useAuth();

  const feedTypeConfig = {
    GENERAL: { 
      label: 'General Update', 
      icon: <MdFeed />, 
      color: '#6366f1',
      bgGradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)'
    },
    EARNINGS_UPDATE: { 
      label: 'Earnings Update', 
      icon: <FaDollarSign />, 
      color: '#10b981',
      bgGradient: 'linear-gradient(135deg, #10b981, #059669)'
    },
    NEW_COMPANY: { 
      label: 'New Company', 
      icon: <FaBuilding />, 
      color: '#f59e0b',
      bgGradient: 'linear-gradient(135deg, #f59e0b, #d97706)'
    },
    PROFIT_ANNOUNCEMENT: { 
      label: 'Profit News', 
      icon: <FaChartLine />, 
      color: '#8b5cf6',
      bgGradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
    },
    MARKET_UPDATE: { 
      label: 'Market Update', 
      icon: <MdTrendingUp />, 
      color: '#ef4444',
      bgGradient: 'linear-gradient(135deg, #ef4444, #dc2626)'
    }
  };

  useEffect(() => {
    fetchFeeds();
    
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchFeeds = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://api.globallequityfinance.com/api/feeds');
      setFeeds(response.data);
    } catch (error) {
      console.error('Error fetching feeds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchFeeds();
    setRefreshing(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const feedDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - feedDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return feedDate.toLocaleDateString();
  };

  const FeedCard = ({ feed, index }) => {
    const feedType = feedTypeConfig[feed.feedType] || feedTypeConfig.GENERAL;
    
    return (
      <div 
        className="feed-card" 
        style={{ animationDelay: `${index * 0.1}s` }}
      >
        <div className="feed-header">
          <div className="feed-type-badge" style={{ background: feedType.bgGradient }}>
            <span className="feed-type-icon">{feedType.icon}</span>
            <span className="feed-type-text">{feedType.label}</span>
            {feed.feedType === 'EARNINGS_UPDATE' && (
              <IoSparkles className="sparkle-icon" />
            )}
          </div>
          <div className="feed-time">
            <FaClock className="time-icon" />
            <span>{formatTimeAgo(feed.createdAt)}</span>
          </div>
        </div>

        {feed.imageUrl && (
          <div className="feed-image-container">
            <img 
              src={`https://api.globallequityfinance.com${feed.imageUrl}`}
              alt="Feed content"
              className="feed-image"
              loading="lazy"
            />
            <div className="image-overlay">
              <div className="overlay-gradient"></div>
            </div>
          </div>
        )}

        <div className="feed-content">
          <p className="feed-description">{feed.description}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="user-feeds-container">
      {/* Header */}
      <div className="feeds-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-title-section">
            <div className="title-icon-wrapper">
              <FaFire className="title-icon" />
              <div className="icon-glow"></div>
            </div>
            <div className="title-text">
              <h1 className="main-title">Latest Updates</h1>
              <p className="subtitle">Stay informed with the latest news</p>
            </div>
          </div>
          
          <button 
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <IoFlash className={`refresh-icon ${refreshing ? 'spinning' : ''}`} />
          </button>
        </div>
      </div>

      {/* Feeds Content */}
      <div className="feeds-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-center">
                <FaBolt className="spinner-icon" />
              </div>
            </div>
            <h3 className="loading-title">Loading Updates</h3>
            <p className="loading-text">Fetching the latest news for you...</p>
          </div>
        ) : feeds.length > 0 ? (
          <div className="feeds-list">
            {feeds.map((feed, index) => (
              <FeedCard key={feed.id} feed={feed} index={index} />
            ))}
          </div>
        ) : (
          <div className="empty-feeds">
            <div className="empty-animation">
              <div className="empty-circle">
                <MdNotifications className="empty-icon" />
              </div>
              <div className="floating-elements">
                <IoSparkles className="float-element element-1" />
                <FaGem className="float-element element-2" />
                <IoTrending className="float-element element-3" />
              </div>
            </div>
            <h3 className="empty-title">No Updates Yet</h3>
            <p className="empty-description">
              Check back soon for the latest news and updates from our team.
            </p>
            <button className="retry-btn" onClick={handleRefresh}>
              <IoFlash className="retry-icon" />
              <span>Refresh</span>
            </button>
          </div>
        )}
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button className="scroll-top-btn" onClick={scrollToTop}>
          <FaArrowUp className="scroll-icon" />
        </button>
      )}

      <BottomNavigation />

      <style jsx>{`
        .user-feeds-container {
          background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
          min-height: 100vh;
          color: #ffffff;
          font-family: 'Inter', system-ui, sans-serif;
          padding-bottom: 100px;
          position: relative;
        }

        .user-feeds-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 400px;
          background: radial-gradient(ellipse at top, rgba(99, 102, 241, 0.15), transparent 70%);
          pointer-events: none;
          z-index: 0;
        }

        .feeds-header {
          position: relative;
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(99, 102, 241, 0.2);
          padding: 20px 16px;
          margin-bottom: 20px;
          overflow: hidden;
        }

        .header-background {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: conic-gradient(from 0deg, transparent, rgba(99, 102, 241, 0.1), transparent);
          animation: rotate-bg 20s linear infinite;
        }

        @keyframes rotate-bg {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .header-content {
          position: relative;
          z-index: 2;
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 600px;
          margin: 0 auto;
        }

        .header-title-section {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .title-icon-wrapper {
          position: relative;
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
        }

        .title-icon {
          font-size: 24px;
          color: white;
          z-index: 2;
        }

        .icon-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 16px;
          opacity: 0.5;
          animation: pulse-glow 2s infinite;
        }

        @keyframes pulse-glow {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        .title-text h1 {
          font-size: 22px;
          font-weight: 800;
          margin: 0;
          background: linear-gradient(135deg, #ffffff, #6366f1);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .title-text p {
          font-size: 14px;
          color: #94a3b8;
          margin: 4px 0 0 0;
        }

        .refresh-btn {
          width: 44px;
          height: 44px;
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 12px;
          color: #6366f1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }

        .refresh-btn:hover:not(:disabled) {
          background: rgba(99, 102, 241, 0.2);
          transform: scale(1.05);
        }

        .refresh-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .refresh-icon {
          font-size: 18px;
          transition: transform 0.3s ease;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .feeds-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 0 16px;
          position: relative;
          z-index: 1;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 80px 20px;
          text-align: center;
        }

        .loading-spinner {
          position: relative;
          width: 80px;
          height: 80px;
          margin-bottom: 24px;
        }

        .spinner-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border: 3px solid rgba(99, 102, 241, 0.2);
          border-radius: 50%;
          border-top: 3px solid #6366f1;
          animation: spin 1.5s linear infinite;
        }

        .spinner-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: pulse 2s infinite;
        }

        .spinner-icon {
          font-size: 20px;
          color: white;
        }

        @keyframes pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.1); }
        }

        .loading-title {
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 8px 0;
          color: #ffffff;
        }

        .loading-text {
          font-size: 14px;
          color: #94a3b8;
          margin: 0;
        }

        .feeds-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .feed-card {
          background: linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.8));
          border-radius: 20px;
          border: 1px solid rgba(99, 102, 241, 0.2);
          overflow: hidden;
          backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          transition: all 0.4s ease;
          animation: slide-up 0.6s ease-out forwards;
          opacity: 0;
          transform: translateY(30px);
        }

        @keyframes slide-up {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .feed-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(99, 102, 241, 0.2);
          border-color: rgba(99, 102, 241, 0.4);
        }

        .feed-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .feed-type-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
          position: relative;
          overflow: hidden;
        }

        .feed-type-badge::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          animation: shimmer 3s infinite;
        }

        @keyframes shimmer {
          0% { left: -100%; }
          20% { left: 100%; }
          100% { left: 100%; }
        }

        .feed-type-icon {
          font-size: 16px;
          z-index: 2;
        }

        .feed-type-text {
          z-index: 2;
        }

        .sparkle-icon {
          font-size: 12px;
          color: #ffd700;
          animation: twinkle 1.5s infinite;
          z-index: 2;
        }

        @keyframes twinkle {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        .feed-time {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #94a3b8;
          font-size: 13px;
        }

        .time-icon {
          font-size: 12px;
        }

        .feed-image-container {
          position: relative;
          width: 100%;
          height: 250px;
          overflow: hidden;
        }

        .feed-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        .feed-card:hover .feed-image {
          transform: scale(1.05);
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 60px;
          pointer-events: none;
        }

        .overlay-gradient {
          width: 100%;
          height: 100%;
          background: linear-gradient(to top, rgba(15, 23, 42, 0.8), transparent);
        }

        .feed-content {
          padding: 20px;
        }

        .feed-description {
          font-size: 15px;
          line-height: 1.6;
          color: #e2e8f0;
          margin: 0;
        }

        .feed-actions {
          display: flex;
          justify-content: space-around;
          padding: 16px 20px;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: transparent;
          border: none;
          color: #94a3b8;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .action-btn:hover {
          color: #6366f1;
          background: rgba(99, 102, 241, 0.1);
          transform: translateY(-1px);
        }

        .action-icon {
          font-size: 16px;
        }

        .like-btn:hover {
          color: #ef4444;
        }

        .like-btn:hover .action-icon {
          animation: heartbeat 0.6s ease;
        }

        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }

        .comment-btn:hover {
          color: #10b981;
        }

        .share-btn:hover {
          color: #8b5cf6;
        }

        .empty-feeds {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 80px 20px;
          text-align: center;
        }

        .empty-animation {
          position: relative;
          margin-bottom: 24px;
        }

        .empty-circle {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .empty-icon {
          font-size: 40px;
          color: white;
        }

        .floating-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }

        .float-element {
          position: absolute;
          font-size: 20px;
          color: #6366f1;
          animation: float-around 4s ease-in-out infinite;
        }

        .element-1 {
          top: 10px;
          right: 10px;
          animation-delay: 0s;
        }

        .element-2 {
          bottom: 10px;
          left: 10px;
          animation-delay: 1.5s;
        }

        .element-3 {
          top: 50%;
          right: -10px;
          animation-delay: 3s;
        }

        @keyframes float-around {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.6; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
        }

        .empty-title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 12px 0;
          color: #ffffff;
        }

        .empty-description {
          font-size: 16px;
          color: #94a3b8;
          margin: 0 0 24px 0;
          line-height: 1.5;
        }

        .retry-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .retry-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
        }

        .retry-icon {
          font-size: 16px;
        }

        .scroll-top-btn {
          position: fixed;
          bottom: 120px;
          right: 20px;
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
          z-index: 1000;
          animation: fade-in 0.3s ease;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .scroll-top-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
        }

        .scroll-icon {
          font-size: 20px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .feeds-header {
            padding: 16px 12px;
          }

          .header-title-section {
            gap: 12px;
          }

          .title-icon-wrapper {
            width: 44px;
            height: 44px;
          }

          .title-icon {
            font-size: 20px;
          }

          .title-text h1 {
            font-size: 18px;
          }

          .title-text p {
            font-size: 13px;
          }

          .refresh-btn {
            width: 40px;
            height: 40px;
          }

          .refresh-icon {
            font-size: 16px;
          }

          .feeds-content {
            padding: 0 12px;
          }

          .feed-card {
            margin-bottom: 16px;
          }

          .feed-header {
            padding: 14px 16px;
          }

          .feed-type-badge {
            font-size: 12px;
            padding: 6px 12px;
          }

          .feed-content {
            padding: 16px;
          }

          .feed-description {
            font-size: 14px;
          }

          .feed-actions {
            padding: 12px 16px;
          }

          .action-btn {
            font-size: 13px;
            padding: 6px 8px;
          }

          .scroll-top-btn {
            bottom: 100px;
            right: 16px;
            width: 44px;
            height: 44px;
          }

          .scroll-icon {
            font-size: 18px;
          }
        }

        @media (max-width: 480px) {
          .feed-image-container {
            height: 200px;
          }

          .feed-actions {
            flex-direction: column;
            gap: 8px;
          }

          .action-btn {
            width: 100%;
            justify-content: center;
          }

          .empty-circle {
            width: 80px;
            height: 80px;
          }

          .empty-icon {
            font-size: 32px;
          }

          .empty-title {
            font-size: 20px;
          }

          .empty-description {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default UserFeeds;